import { createSlice } from '@reduxjs/toolkit';

import {
  fetchCompleteDraftFields,
  fetchDraftById,
  fetchDraftStamps,
} from './DraftViewAPI';
import { RootState } from '../../store';

// Define a type for the slice state
interface DocumentViewState {
  draft: {
    loading: boolean;
    data: any;
  };
  activeTab: string;
  optionalFields: {
    loading: boolean;
    data: any;
    fields: any;
    approvalFields: any;
    fetchingOptionalField: boolean;
  };
  dynamicFields: any;
  canEdit: boolean;
  selectedOption: string | null;
  tabs: any;
  toggleResponseData: any;
  draftStamps: {
    data: any;
    loading: boolean;
  };
}

// Define the initial state using that type
const initialState: DocumentViewState = {
  draft: {
    loading: false,
    data: null,
  },
  activeTab: 'complete_draft',
  optionalFields: {
    loading: false,
    data: [],
    fields: null,
    approvalFields: null,
    fetchingOptionalField: false,
  },
  dynamicFields: [],
  canEdit: false,
  selectedOption: null,
  tabs: [],
  toggleResponseData: null,
  draftStamps: {
    data: null,
    loading: false,
  },
};

export const draftView = createSlice({
  name: 'draftView',
  // `createSlice` will infer the state type from the `initialState` argument
  initialState,
  reducers: {
    setActiveTab: (state, actions) => {
      state.activeTab = actions.payload;
    },
    setDynamicFields: (state, action) => {
      state.dynamicFields = action.payload;
    },
    setFetchingOptionalField: (state, action) => {
      state.optionalFields.fetchingOptionalField = action.payload;
    },
    setCanEdit: (state, action) => {
      state.canEdit = action.payload;
    },
    setSelectedOption: (state, action) => {
      state.selectedOption = action.payload;
    },
    setTabs: (state, action) => {
      state.tabs = action.payload;
    },
    setToggleResponseData: (state, action) => {
      state.toggleResponseData = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(
      fetchCompleteDraftFields.fulfilled,
      (state, { payload }) => {
        state.optionalFields.loading = false;
        if (payload.optionalFields) {
          state.optionalFields.data = payload.data;
          state.optionalFields.fetchingOptionalField = true;
        } else {
          state.optionalFields.fields = payload.data;
          state.optionalFields.approvalFields = payload.approvalData;
          state.optionalFields.data = [];
        }
      }
    );
    builder.addCase(fetchCompleteDraftFields.pending, (state) => {
      state.optionalFields.loading = true;
    });
    builder.addCase(fetchCompleteDraftFields.rejected, (state) => {
      state.optionalFields.loading = false;
    });
    builder.addCase(fetchDraftById.pending, (state) => {
      state.draft.loading = true;
    });
    builder.addCase(fetchDraftById.fulfilled, (state, { payload }) => {
      state.draft.loading = true;
      state.draft.data = payload;
    });
    builder.addCase(fetchDraftById.rejected, (state) => {
      state.draft.loading = true;
    });
    builder.addCase(fetchDraftStamps.pending, (state) => {
      state.draftStamps.loading = true;
    });
    builder.addCase(fetchDraftStamps.fulfilled, (state, { payload }) => {
      state.draftStamps.loading = false;
      state.draftStamps.data = payload;
    });
    builder.addCase(fetchDraftStamps.rejected, (state) => {
      state.draftStamps.loading = false;
    });
  },
});

export const {
  setActiveTab,
  setDynamicFields,
  setFetchingOptionalField,
  setSelectedOption,
  setTabs,
  setToggleResponseData,
} = draftView.actions;

export const selectOptionalFields = (state: RootState) =>
  state.draftView.optionalFields;
export const selectDynamicFields = (state: RootState) =>
  state.draftView.dynamicFields;
export const selectDraftById = (state: RootState) => state.draftView.draft;
export const selectDraftStamps = (state: RootState) =>
  state.draftView.draftStamps;
export const draftViewState = (state: RootState) => state.draftView;
// Other code such as selectors can use the imported `RootState` type

export default draftView.reducer;
